import React, { useEffect, useMemo, useRef, useState } from "react";
import {
	Alert,
	Button,
	Card,
  Container,
  Form,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import api from "../api/api";
import TherapistMilestones from "./TherapistMilestones";
import "../css/ClientList.css";

const ClientListComponent = () => {
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchType, setSearchType] = useState("name");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const isPracticeOnboarding =
    clients.length > 0 && clients.every((client) => client.is_practice);
	const [clientListMinHeight, setClientListMinHeight] = useState(null);
	const clientListBodyRef = useRef(null);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        setIsLoading(true);
				setError(null);

        const response = await api.get("/users/clients/");

				const sortedClients = [...response.data].sort((a, b) => {
					const lastNameComparison = (a.last_name || "").localeCompare(
						b.last_name || "",
        );

					if (lastNameComparison !== 0) {
						return lastNameComparison;
					}

					return (a.first_name || "").localeCompare(
						b.first_name || "",
					);
				});

        setClients(sortedClients);
      } catch (err) {
        setError("Failed to fetch clients. Please try again later.");
        console.error("Error fetching clients:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchClients();
  }, []);

	const filteredClients = useMemo(() => {
		const normalizedSearchTerm = searchTerm.trim().toLowerCase();
		const visibleClients = clients;

		if (!normalizedSearchTerm) {
			return visibleClients;
    }

		return visibleClients.filter((client) => {
      const firstName = (client.first_name || "").toLowerCase();
      const lastName = (client.last_name || "").toLowerCase();
      const fullName = `${firstName} ${lastName}`;
      const reverseName = `${lastName} ${firstName}`;

			if (searchType === "name") {
          return (
					firstName.includes(normalizedSearchTerm) ||
					lastName.includes(normalizedSearchTerm) ||
					fullName.includes(normalizedSearchTerm) ||
					reverseName.includes(normalizedSearchTerm)
          );
			}

			if (searchType === "dob" && client.date_of_birth) {
				const rawDate = client.date_of_birth.slice(0, 10);
				const dateOfBirth = parseISO(rawDate);
				const formattedDate = format(dateOfBirth, "dd/MM/yyyy");
				const alternativeDate = format(dateOfBirth, "MM/dd/yyyy");

          return (
					rawDate.includes(normalizedSearchTerm) ||
					formattedDate.includes(normalizedSearchTerm) ||
					alternativeDate.includes(normalizedSearchTerm)
          );
        }

          return false;
    });
	}, [clients, searchTerm, searchType]);


	const handleSearchTypeChange = (event) => {
		setSearchType(event.target.value);
		setSearchTerm("");
		setClientListMinHeight(null);
  };

	const handleSearchChange = (event) => {
		const nextSearchTerm = event.target.value;

		if (nextSearchTerm && !searchTerm && clientListBodyRef.current) {
			setClientListMinHeight(clientListBodyRef.current.offsetHeight);
		}

		if (!nextSearchTerm) {
			setClientListMinHeight(null);
		}

		setSearchTerm(nextSearchTerm);
	};

  const renderSearchInput = () => {
		if (searchType === "dob") {
        return (
          <Form.Control
            type="date"
            value={searchTerm}
					onChange={handleSearchChange}
					aria-label="Search by date of birth"
          />
        );
    }

		return (
			<Form.Control
				type="search"
				value={searchTerm}
				onChange={handleSearchChange}
				placeholder="Search by name"
				aria-label="Search clients by name"
			/>
		);
  };

  if (isLoading) {
    return (
			<Container fluid className="client-list-page">
				<div className="client-list-status">
					Loading clients...
				</div>
      </Container>
    );
  }

  if (error) {
    return (
			<Container fluid className="client-list-page">
				<Alert variant="danger" className="client-list-alert">
					{error}
				</Alert>
      </Container>
    );
  }

  return (
		<Container fluid className="client-list-page">
      {!isPracticeOnboarding && <TherapistMilestones />}

			<Card className="client-list-card">
				<Card.Header className="client-list-header">
					<h2 className="client-list-title">
						{isPracticeOnboarding ? "Test Client" : "Clients"}
					</h2>

          {!isPracticeOnboarding && (
            <>
    					<div className="client-list-filters">
                      <Form.Select
                        value={searchType}
                        onChange={handleSearchTypeChange}
    							aria-label="Choose client search type"
    							className="client-list-search-type"
                      >
                        <option value="name">Name</option>
                        <option value="dob">Date of Birth</option>
                      </Form.Select>

    						<div className="client-list-search">
    							{renderSearchInput()}
    						</div>
    					</div>

                      <Button
    						as={Link}
    						to="/client/create"
                        variant="primary"
    						className="client-list-add-button"
                      >
                        Add Client
                      </Button>
            </>
          )}
        </Card.Header>


				<Card.Body
					ref={clientListBodyRef}
					className="client-list-body"
					style={clientListMinHeight ? { minHeight: clientListMinHeight } : undefined}
				>
          {filteredClients.length > 0 ? (
						<div className="client-list-tiles">
              {filteredClients.map((client) => (
								<Link
                  key={client.id}
									to={`/client/${client.id}`}
									className="client-list-item"
                  data-walkthrough={
                    client.is_practice ? "practice-client" : undefined
                  }
                >
									<span className="client-list-name">
                    {client.is_practice
                      ? "Julia Smith"
                      : `${client.last_name}, ${client.first_name}`}
                  </span>

									<span className="client-list-dob">
                    {client.is_practice
                      ? "DOB 30/04/1984"
                      : client.date_of_birth
										  ? format(
											  parseISO(
												  client.date_of_birth.slice(0, 10),
											  ),
											  "dd/MM/yyyy",
										    )
                        : "N/A"}
                  </span>
								</Link>
              ))}
            </div>
          ) : (
						<div className="client-list-empty">
              {clients.length === 0 ? (
                <>
                  <h4 className="client-list-empty-title">You have no active clients.</h4>
                  <p className="client-list-empty-subtitle">Who can you add today?</p>
                </>
              ) : (
                <p className="client-list-empty-subtitle">No clients match your current search or filters.</p>
              )}
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default ClientListComponent;
